import { TCAppConfig } from '../../../../../shared/app-configs/tc-app-configs';
import { useTransactionCertificateStore } from '../../../../../store/transaction-certificates.store';

/**
 * Fetch product component details using the given API caller.
 *
 * @param executeGet - The API call function from your custom hook.
 * @param tcId - The TE_ID.
 * @param shipmentId - The shipment identifier.
 * @param productId - The product identifier.
 * @param inputTcId - The input TC identifier.
 * @param inputTcComponentId - The input TC component identifier.
 * @returns A promise that resolves with the product component details.
 * @throws An error if the fetch fails.
 */
export async function fetchProductComponentDetails(
  executeGet: (url: string, params: Record<string, any>) => Promise<any>,
  tcId: string,
  shipmentId: string,
  productId: string,
  inputTcId: string,
  inputTcComponentId: string
): Promise<any> {
  if (!inputTcComponentId) return null;

  try {
    const response = await executeGet(TCAppConfig.GET_INPUT_COMPONENT_DETAILS_NEW, {
      tcId,
      shipmentId,
      productId,
      inputTcId,
      inputTcComponentId,
    });
    return response;
  } catch (err) {
    console.error('Error fetching product component details:', err);
    throw new Error('Failed to load product component details.');
  }
}

// Input data interface for component data
export interface InputComponentData {
    inputTcComponentInformationOrder: string;
    inputTcProductNo: string;
    inputTcProductComponentNo: string;
    inputTcComponentDetail: string;
    inputTcComponentInfoTrimsExcluded: string;
    inputTcComponentInfoAssociatedTc: string;
    inputTcComponentInfoFiberLength: string;
    inputTcComponentInfoYarnCount: string;
    inputTcComponentInfoFabricWeightAndConstruct: string;
    inputTcComponentInfoFabricWeightOfFinishedProduct: string;
    inputTcComponentInfoAdditional: string;
    inputTcComponentNetShippingWeight: string;
    inputTcComponentSupplementaryWeight: string;
    standardInfo: Array<{
      inputTcComponentStandardInformationOrder: string;
      inputTcComponentStandardLabelGrade: string;
      inputTcComponentCertifiedWeight: string;
      inputTcComponentCertifiedWeightUsed: string;
      // additional fields (if any) can be included
    }>;
    rawMaterialInfo: Array<{
      inputComponentRawMaterialInformationOrder: string;
      inputTcComponentRawMaterialCode: string;
      inputTcComponentRawMaterialPercentage: string;
      // additional fields (if any) can be included
    }>;
  }
  
  // Output (post payload) interface
  export interface PostComponentPayload {
    tcId: string;
    inputTcComponentInformation: Array<{
      inputTcComponentInformationOrder: string;
      inputTcProductNo: string;
      inputTcProductComponentNo: string;
      inputTcComponentDetail: string;
      inputTcComponentInfoTrimsExcluded: string;
      inputTcComponentInfoAssociatedTc: string;
      inputTcComponentInfoFiberLength: string;
      inputTcComponentInfoYarnCount: string;
      inputTcComponentInfoFabricWeightAndConstruct: string;
      inputTcComponentInfoFabricWeightOfFinishedProduct: string;
      inputTcComponentNetShippingWeight: number|null;
      inputTcComponentSupplementaryWeight: number|null;
      inputTcComponentInfoAdditional: string;
      inputTcComponentStandardInformation: Array<{
        inputTcComponentStandardInformationOrder: string;
        inputTcComponentStandardLabelGrade: string;
        inputTcComponentCertifiedWeight: number;
        inputTcComponentCertifiedWeightUsed: number;
        additionalProp1: Record<string, any>;
      }>;
      inputComponentRawMaterialInformation: Array<{
        inputComponentRawMaterialInformationOrder: string;
        inputTcComponentRawMaterialPercentage: number;
        inputTcComponentRawMaterialCode: string;
        additionalProp1: Record<string, any>;
      }>;
      additionalProp1: Record<string, any>;
    }>;
    additionalProp1: Record<string, any>;
  }
  export const getProductDataByProductNo = (
    productData: Array<any>,
    productNo: string
  ) => {
    return productData
      // Filter products where the product number matches.
      .filter(product => product.inputTcProductNo === productNo)
      // Return only the required fields.
      .map(product => ({
        shipmentId: product.shipmentId,
        productId: product.productId,
        inputTcId: product.inputTcId,
      }));
  };
  /**
   * Maps an input component data object into the desired post payload.
   *
   * @param input - The input component data.
   * @param tcId - The top-level tcId value.
   * @returns The mapped PostComponentPayload.
   */
  export const mapInputComponentData = (
    input: any,
    tcId: string,
    inputTcComponentId?: string
  ): PostComponentPayload => {
    const parseNumber = (value: any) =>
      value !== null && value !== undefined && value !== ''
        ? Number(value)
        : 0; // Default to 0 if null/undefined/empty (or change to undefined if field is optional)
  
    return {
      tcId,
      ...(inputTcComponentId && { inputTcComponentId }), // Only include if available
      inputTcComponentInformation: [
        {
          inputTcComponentInformationOrder: input.inputTcComponentInformationOrder,
          inputTcProductNo: input.inputTcProductNo,
          inputTcProductComponentNo: input.inputTcProductComponentNo,
          inputTcComponentDetail: input.inputTcComponentDetail,
          inputTcComponentInfoTrimsExcluded: input.inputTcComponentInfoTrimsExcluded,
          inputTcComponentInfoAssociatedTc: input.inputTcComponentInfoAssociatedTc,
          inputTcComponentInfoFiberLength: input.inputTcComponentInfoFiberLength,
          inputTcComponentInfoYarnCount: input.inputTcComponentInfoYarnCount,
          inputTcComponentInfoFabricWeightAndConstruct:
            input.inputTcComponentInfoFabricWeightAndConstruct,
          inputTcComponentInfoFabricWeightOfFinishedProduct:
            input.inputTcComponentInfoFabricWeightOfFinishedProduct,
          inputTcComponentNetShippingWeight: (input.inputTcComponentNetShippingWeight && input.inputTcComponentNetShippingWeight!=="")?parseNumber(
            input.inputTcComponentNetShippingWeight
          ):null,
          inputTcComponentSupplementaryWeight: (input.inputTcComponentSupplementaryWeight && input.inputTcComponentSupplementaryWeight!=="")?parseNumber(
            input.inputTcComponentSupplementaryWeight
          ):null,
          inputTcComponentInfoAdditional: input.inputTcComponentInfoAdditional,
          inputTcComponentStandardInformation: (input.standardInfo || []).map(
            (item: {
              inputTcComponentStandardInformationOrder: any;
              inputTcComponentStandardLabelGrade: any;
              inputTcComponentCertifiedWeight: any;
              inputTcComponentCertifiedWeightUsed: any;
            }) => ({
              inputTcComponentStandardInformationOrder:
                item.inputTcComponentStandardInformationOrder,
              inputTcComponentStandardLabelGrade:
                item.inputTcComponentStandardLabelGrade,
              inputTcComponentStandardLabelGradeDescription: useTransactionCertificateStore.getState().getLabelGrades.find((option: any) => option.value === item.inputTcComponentStandardLabelGrade)?.label || "",
              inputTcComponentCertifiedWeight: (item.inputTcComponentCertifiedWeight && item.inputTcComponentCertifiedWeight!=="")?parseNumber(
                item.inputTcComponentCertifiedWeight
              ):null,
              inputTcComponentCertifiedWeightUsed: (item.inputTcComponentCertifiedWeightUsed && item.inputTcComponentCertifiedWeightUsed!=="")? parseNumber(
                item.inputTcComponentCertifiedWeightUsed
              ):null,
              additionalProp1: {}, // Placeholder
            })
          ),
          inputComponentRawMaterialInformation: (input.rawMaterialInfo || []).map(
            (item: {
              inputComponentRawMaterialInformationOrder: any;
              inputTcComponentRawMaterialPercentage: any;
              inputTcComponentRawMaterialCode: any;
            }) => ({
              inputComponentRawMaterialInformationOrder:
                item.inputComponentRawMaterialInformationOrder,
              inputTcComponentRawMaterialPercentage: (item.inputTcComponentRawMaterialPercentage && item.inputTcComponentRawMaterialPercentage!=="") ?parseNumber(
                item.inputTcComponentRawMaterialPercentage
              ):null,
              inputTcComponentRawMaterialCode:
                item.inputTcComponentRawMaterialCode,
              inputTcComponentRawMaterialCodeDescription: useTransactionCertificateStore.getState().getRawMtrlCodes.find((option: any) => option.value === item.inputTcComponentRawMaterialCode)?.label || "",
              additionalProp1: {}, // Placeholder
            })
          ),
          additionalProp1: {}, // Placeholder
        },
      ],
      additionalProp1: {}, // Placeholder
    };
  };
  

 export const mapComponentResponseToState = (response: any) => {
    const component = response.inputTcComponentInformation[0]; // First component
  
    const componentInfo = {
      inputTcComponentInformationOrder: component.inputTcComponentInformationOrder,
      inputTcProductNo: component.inputTcProductNo,
      inputTcProductComponentNo: component.inputTcProductComponentNo,
      inputTcComponentDetail: component.inputTcComponentDetail,
      inputTcComponentInfoTrimsExcluded: component.inputTcComponentInfoTrimsExcluded,
      inputTcComponentInfoAssociatedTc: component.inputTcComponentInfoAssociatedTc,
      inputTcComponentInfoFiberLength: component.inputTcComponentInfoFiberLength,
      inputTcComponentInfoYarnCount: component.inputTcComponentInfoYarnCount,
      inputTcComponentInfoFabricWeightAndConstruct: component.inputTcComponentInfoFabricWeightAndConstruct,
      inputTcComponentInfoFabricWeightOfFinishedProduct: component.inputTcComponentInfoFabricWeightOfFinishedProduct,
      inputTcComponentNetShippingWeight: component.inputTcComponentNetShippingWeight ?? '', // optional, can handle accordingly
      inputTcComponentSupplementaryWeight: component.inputTcComponentSupplementaryWeight ?? '',
      inputTcComponentInfoAdditional: component.inputTcComponentInfoAdditional,
    };
  
    const standardInfo = (component.inputTcComponentStandardInformation || []).map(
      (std: any) => ({
        inputTcComponentStandardInformationOrder: std.inputTcComponentStandardInformationOrder,
        inputTcComponentStandardLabelGrade: std.inputTcComponentStandardLabelGrade,
        inputTcComponentCertifiedWeight: std.inputTcComponentCertifiedWeight,
        inputTcComponentCertifiedWeightUsed: std.inputTcComponentCertifiedWeightUsed,
      })
    );
  
    const rawMaterialInfo = (component.inputComponentRawMaterialInformation || []).map(
      (raw: any) => ({
        inputComponentRawMaterialInformationOrder: raw.inputComponentRawMaterialInformationOrder,
        inputTcComponentRawMaterialPercentage: raw.inputTcComponentRawMaterialPercentage,
        inputTcComponentRawMaterialCode: raw.inputTcComponentRawMaterialCode,
      })
    );
  
    return {
      componentInfo,
      standardInfo,
      rawMaterialInfo,
    };
  };
  